import { loadXLSX } from './lazylib.js';

const NUMERIC_CELL_VALUE = /^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/;

const TEXT_TAGS = ['DIV', 'SPAN', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

export function parseExcelNumber(value) {
    if (typeof value !== 'string') return null;

    const text = value.trim();
    if (!NUMERIC_CELL_VALUE.test(text)) return null;

    const number = Number(text.replace(/,/g, ''));
    return Number.isFinite(number) ? number : null;
}

/**
 * Export every table in `container` as one .xlsx sheet, with the loose text
 * before the first and after the last table kept as rows (company name, title,
 * remarks, approval lines). SheetJS is fetched on first use.
 *
 * `container` may live in another document (an iframe preview, a DOMParser
 * result), so the tree walk is done through its own document.
 */
export async function exportHtmlTablesToExcel(container, filename) {
    const xlsx = await loadXLSX();
    const clone = container.cloneNode(true);
    clone.querySelectorAll('img, style, script').forEach((el) => el.remove());

    const tables = clone.querySelectorAll('table');
    const workbook = xlsx.utils.book_new();

    if (!tables.length) {
        const empty = xlsx.utils.aoa_to_sheet([['No tabular data found in the preview.']]);
        xlsx.utils.book_append_sheet(workbook, empty, 'Sheet1');
        xlsx.writeFile(workbook, filename);
        return;
    }

    const ownerDoc = clone.ownerDocument || document;
    const nodeFilter = (ownerDoc.defaultView && ownerDoc.defaultView.NodeFilter) || NodeFilter;
    const isTextNode = (node) =>
        TEXT_TAGS.includes(node.tagName)
        && node.children.length === 0
        && (node.textContent || '').trim();

    const rows = [];
    const firstTable = tables[0];
    const before = ownerDoc.createTreeWalker(clone, nodeFilter.SHOW_ELEMENT);
    let node;
    while ((node = before.nextNode())) {
        if (node === firstTable) break;
        if (isTextNode(node)) rows.push([node.textContent.trim()]);
    }
    if (rows.length) rows.push([]);

    tables.forEach((table, idx) => {
        const caption = table.querySelector('caption');
        if (caption) {
            rows.push([]);
            rows.push([(caption.textContent || '').trim()]);
        }
        // Convert HTML table to sheet, preserving non-numeric values as text.
        const sheet = tableToExcelWorksheet(xlsx, table);
        xlsx.utils.sheet_to_json(sheet, { header: 1 }).forEach((row) => rows.push(row));
        if (idx < tables.length - 1) rows.push([]);
    });

    const lastTable = tables[tables.length - 1];
    let afterLastTable = false;
    const after = ownerDoc.createTreeWalker(clone, nodeFilter.SHOW_ELEMENT);
    while ((node = after.nextNode())) {
        if (node === lastTable) { afterLastTable = true; continue; }
        if (!afterLastTable) continue;
        if (node.closest && node.closest('table')) continue;
        if (isTextNode(node)) rows.push([node.textContent.trim()]);
    }

    const merged = xlsx.utils.aoa_to_sheet(rows);
    xlsx.utils.book_append_sheet(workbook, merged, 'Sheet1');
    xlsx.writeFile(workbook, filename);
}

export function tableToExcelWorksheet(xlsx, table) {
    const worksheet = xlsx.utils.table_to_sheet(table, { raw: true });

    Object.keys(worksheet).forEach(function (address) {
        if (address.startsWith('!')) return;

        const cell = worksheet[address];
        const number = parseExcelNumber(cell.v);
        if (number === null) return;

        cell.t = 'n';
        cell.v = number;
    });

    return worksheet;
}
